// firstPassRate.js - Modal content for First Pass Rate KPI
// Place at: src/components/modalContent/productionMilestonesModalComponents/firstPassRate.js

import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import useFirstPassRateData from "../../../hooks/useFirstPassRateData";
import useTableSort from "../../../hooks/useTableSort";
import SortableTableHeader from "../../modalContent/SortableTableHeader";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  return (
    <div className="pml-chart-tooltip">
      <div className="pml-chart-tooltip-date">{point.label}</div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: "#cc0000" }}
        />
        {payload[0].value}% first pass rate
      </div>
    </div>
  );
};

const FirstPassRate = ({ goals }) => {
  const { metrics, graph, tableFailedJobs, tableUtilities, loading, error } =
    useFirstPassRateData();

  // ── Table sorting — must be called before any early returns ───────────────
  const { sortedData, requestSort, getSortClass } = useTableSort(
    tableFailedJobs,
    {
      initialKey: "day",
      initialDirection: "desc",
    },
  );

  // ── Loading / error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="pml-modal-flex-content pml-modal-loading">
        Loading first pass rate data…
      </div>
    );
  }

  if (error) {
    return (
      <div className="pml-modal-flex-content pml-modal-error">{error}</div>
    );
  }

  // goals prop allows the parent to override the live target if needed;
  // falls back to the server-derived targetFpr from the hook.
  const target = goals?.firstPassRate || metrics.targetFpr;

  const { currentMonthFpr, weeksMet, weeksUnder } = metrics;

  const currentVariant =
    currentMonthFpr >= target
      ? "ok"
      : currentMonthFpr >= target - 3
        ? "amber"
        : "alert";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Stat band */}
      <div className="pml-modal-stat-band pml-modal-stat-band--4">
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Current Month</span>
          <span
            className={`pml-modal-stat-value pml-modal-stat-value--${currentVariant}`}
          >
            {currentMonthFpr}%
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Target</span>
          <span className="pml-modal-stat-value">{target}%</span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Weeks Met/Exceeding Goal</span>
          <span className="pml-modal-stat-value pml-modal-stat-value--ok">
            {weeksMet}
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Weeks Under Goal</span>
          <span
            className={`pml-modal-stat-value ${weeksUnder > 0 ? "pml-modal-stat-value--alert" : "pml-modal-stat-value--ok"}`}
          >
            {weeksUnder}
          </span>
        </div>
      </div>

      {/* Line chart — daily FPR trend, current month */}
      <p className="pml-modal-sub-title">FPR Trend This Month</p>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={graph}
            margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={{ stroke: "#e2e8f0" }}
              interval={Math.max(0, Math.ceil(graph.length / 10) - 1)}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v}%`}
              domain={[84, 100]}
              width={38}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={target}
              stroke="#1a1a1a"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              label={{
                value: `Target ${target}%`,
                position: "insideTopRight",
                fontSize: 11,
                fill: "#1a1a1a",
              }}
            />
            {/* connectNulls=false stops the line at the last real day */}
            <Line
              type="monotone"
              dataKey="fpr"
              stroke="#cc0000"
              strokeWidth={2}
              dot={{ r: 3, fill: "#cc0000", strokeWidth: 0 }}
              activeDot={{ r: 6 }}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Two-column lower section */}
      <div className="fpr-lower-grid">
        {/* Failed jobs this month */}
        <div>
          <p className="pml-modal-sub-title">Failed Jobs This Month</p>
          <div className="pml-modal-scroll-wrap pml-table-auto-height">
            <table className="pml-modal-table">
              <thead>
                <tr>
                  <SortableTableHeader
                    columnKey="woNum"
                    onSort={requestSort}
                    sortClass={getSortClass("woNum")}
                  >
                    Work Order
                  </SortableTableHeader>
                  <SortableTableHeader
                    columnKey="itemNum"
                    onSort={requestSort}
                    sortClass={getSortClass("itemNum")}
                  >
                    Item
                  </SortableTableHeader>
                  <SortableTableHeader
                    columnKey="day"
                    onSort={requestSort}
                    sortClass={getSortClass("day")}
                  >
                    Date
                  </SortableTableHeader>
                  <th>Failed At</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="pml-modal-table-empty">
                      No failed jobs this month.
                    </td>
                  </tr>
                ) : (
                  sortedData.map((job) => (
                    <tr key={`${job.woNum}-${job.batchNum}`}>
                      <td>{job.woNum}</td>
                      <td>{job.itemNum}</td>
                      <td>{job.day}</td>
                      <td className="pml-variance-cell--neg">
                        {job.failedStations.join(", ")}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Utility failure counts */}
        <div>
          <p className="pml-modal-sub-title">
            Utilities Failure Count (This Month)
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {tableUtilities.map((f) => (
              <div key={f.utility}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}
                  >
                    {f.utility}
                  </span>
                  <span
                    style={{ fontSize: 13, color: "#718096", fontWeight: 500 }}
                  >
                    {f.failCount} ({f.failPct}%)
                  </span>
                </div>
                <div className="pml-progress-bar">
                  <div
                    className="pml-progress-fill"
                    style={{
                      width: `${f.failPct}%`,
                      background: "#cc0000",
                      opacity: 0.7,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstPassRate;
